<template>
    <Dialog v-model:open="addIntegrationDialogOpen">
        <DialogTrigger>
            <slot></slot>
        </DialogTrigger>
        <DialogContent class="max-w-5xl gap-0 max-h-[95dvh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle>
                    {{ $t("integrations.add_integration_dialog.title") }}
                </DialogTitle>
                <DialogDescription>
                    {{ $t("integrations.add_integration_dialog.description") }}
                </DialogDescription>
            </DialogHeader>

            <div class="mb-6 gap-6 grid grid-cols-2">
                <IntegrationCard gradient-class="google-gradient" :paid="false">
                    <template #logo>
                        <GoogleCalendarLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.google.calendar") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.google.description") }}
                    </template>

                    <template #cta>
                        <GoogleDetailModal
                            :requires-sub="false"
                        ></GoogleDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="icloud-gradient" :paid="false">
                    <template #logo>
                        <div
                            class="rounded-lg bg-white shadow-sm flex h-[56px] w-[56px] items-center justify-center"
                        >
                            <img
                                height="18"
                                width="32"
                                src="https://zaptime.s3.eu-central-1.amazonaws.com/application/assets/img/icloud-calendar.png"
                            />
                        </div>
                    </template>

                    <template #name>
                        {{ $t("integrations.icloud_calendar.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.icloud_calendar.description") }}
                    </template>

                    <template #cta>
                        <ICloudDetailModal
                            :requires-sub="false"
                        ></ICloudDetailModal
                    ></template>
                </IntegrationCard>

                <IntegrationCard
                    gradient-class="microsoft-gradient"
                    :paid="false"
                >
                    <template #logo>
                        <MicrosoftLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.microsoft.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.microsoft.description") }}
                    </template>

                    <template #cta>
                        <MicrosoftDetailModal
                            :requires-sub="false"
                        ></MicrosoftDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="stripe-gradient" :paid="false">
                    <template #logo>
                        <ZoomLogo class="w-16" />
                    </template>

                    <template #name> Zoom</template>

                    <template #description>
                        {{ $t("integrations.zoom.description") }}
                    </template>

                    <template #cta>
                        <ZoomDetailModal
                            :requires-sub="false"
                        ></ZoomDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard
                    gradient-class="microsoft-teams-gradient"
                    :paid="false"
                >
                    <template #logo>
                        <MicrosoftTeamsLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.microsoft_teams.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.microsoft_teams.description") }}
                    </template>

                    <template #cta>
                        <MicrosoftTeamsDetailModal
                            :requires-sub="false"
                        ></MicrosoftTeamsDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard
                    gradient-class="pipedrive-gradient"
                    :paid="false"
                    v-if="isAdmin || userId === 400"
                >
                    <template #logo>
                        <PipedriveLogo class="w-16" />
                    </template>

                    <template #name> Pipedrive</template>

                    <template #description>
                        {{ $t("integrations.pipedrive.description") }}
                    </template>

                    <template #cta>
                        <PipedriveDetailModal
                            :requires-sub="false"
                        ></PipedriveDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="ecm-gradient" :paid="true">
                    <template #logo>
                        <EcomailLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.ecomail.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.ecomail.description") }}
                    </template>

                    <template #cta>
                        <EcomailDetailModal></EcomailDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="close-gradient" :paid="true">
                    <template #logo>
                        <CloseLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.close.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.close.description") }}
                    </template>

                    <template #cta>
                        <CloseDetailModal></CloseDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="slack-gradient" :paid="true">
                    <template #logo>
                        <SlackLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.slack.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.slack.description") }}
                    </template>

                    <template #cta>
                        <SlackDetailModal></SlackDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="raynet-gradient" :paid="true">
                    <template #logo>
                        <div
                            class="rounded-lg bg-white shadow-sm flex h-[56px] w-[56px] items-center justify-center"
                        >
                            <img
                                height="18"
                                width="32"
                                src="https://d39xau2r7ekvda.cloudfront.net/application/assets/img/Raynet.png"
                            />
                        </div>
                    </template>

                    <template #name>
                        {{ $t("integrations.raynet.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.raynet.description") }}
                    </template>

                    <template #cta>
                        <RaynetDetailModal></RaynetDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="freelo-gradient" :paid="true">
                    <template #logo>
                        <FreeloLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.freelo.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.freelo.description") }}
                    </template>
                    <template #cta>
                        <FreeloDetailModal></FreeloDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="stripe-gradient" :paid="true">
                    <template #logo>
                        <StripeLogo class="w-16" />
                    </template>

                    <template #name> Stripe</template>

                    <template #description>
                        {{ $t("integrations.stripe.description") }}
                    </template>

                    <template #cta>
                        <StripeDetailModal></StripeDetailModal>
                    </template>
                </IntegrationCard>

                <!-- <IntegrationCard gradient-class="hubspot-gradient" :paid="true">
                    <template #logo>
                        <HubspotLogo class="w-16" />
                    </template>

                    <template #name> HubSpot</template>

                    <template #description>
                        {{ $t("integrations.hubspot.description") }}
                    </template>

                    <template #cta>
                        <HubspotDetailModal
                            :requires-sub="false"
                        ></HubspotDetailModal>
                    </template>
                </IntegrationCard> -->

                <IntegrationCard gradient-class="zaptime-gradient" :paid="true">
                    <template #logo>
                        <Webhook :size="56" />
                    </template>

                    <template #name>{{
                        $t("integrations.webhook.title")
                    }}</template>

                    <template #description>
                        {{ $t("integrations.webhook.description") }}
                    </template>

                    <template #cta>
                        <WebhookDetailModal
                            @integration-created="handleIntegrationCreated"
                        ></WebhookDetailModal>
                    </template>
                </IntegrationCard>

                <IntegrationCard gradient-class="mcp-gradient" :paid="true">
                    <template #logo>
                        <Bot :size="56" />
                    </template>

                    <template #name>{{
                        $t("integrations.mcp.title")
                    }}</template>

                    <template #description>
                        {{ $t("integrations.mcp.description") }}
                    </template>

                    <template #cta>
                        <McpDetailModal></McpDetailModal>
                    </template>
                </IntegrationCard>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import IntegrationCard from "./IntegrationCard.vue";

// Integration Logos
import CloseLogo from "./logos/Close.svg";
import EcomailLogo from "./logos/Ecomail.svg";
import SlackLogo from "./logos/Slack.svg";
import FreeloLogo from "./logos/Freelo.svg";
import StripeLogo from "./logos/Stripe.svg";
import GoogleCalendarLogo from "./logos/GoogleCalendar.svg";
import ZoomLogo from "./logos/Zoom.svg";
import PipedriveLogo from "./logos/Pipedrive.svg";
import GoogleDetailModal from "./DetailModals/GoogleDetailModal.vue";
import ZoomDetailModal from "./DetailModals/ZoomDetailModal.vue";
import ICloudDetailModal from "./DetailModals/ICloudDetailModal.vue";
import MicrosoftDetailModal from "./DetailModals/MicrosoftDetailModal.vue";
import MicrosoftLogo from "./logos/Microsoft.svg";
import MicrosoftTeamsDetailModal from "./DetailModals/MicrosoftTeamsDetailModal.vue";
import MicrosoftTeamsLogo from "./logos/MicrosoftTeams.svg";
import EcomailDetailModal from "./DetailModals/EcomailDetailModal.vue";
import CloseDetailModal from "./DetailModals/CloseDetailModal.vue";
import RaynetDetailModal from "./DetailModals/RaynetDetailModal.vue";
import FreeloDetailModal from "./DetailModals/FreeloDetailModal.vue";
import StripeDetailModal from "./DetailModals/StripeDetailModal.vue";
import SlackDetailModal from "./DetailModals/SlackDetailModal.vue";
import PipedriveDetailModal from "./DetailModals/PipedriveDetailModal.vue";
import HubspotDetailModal from "./DetailModals/HubspotDetailModal.vue";
import HubspotLogo from "./logos/Hubspot.svg";
import { computed, ref } from "vue";
import { usePage } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import WebhookDetailModal from "./DetailModals/WebhookDetailModal.vue";
import McpDetailModal from "./DetailModals/McpDetailModal.vue";
import { Bot, Webhook } from "lucide-vue-next";

type SharedData = {
    userId: number;
    isAdmin: boolean;
};

const page = usePage<SharedData>();
const isAdmin = computed((): any => page.props.isAdmin);
const userId = computed((): any => page.props.userId);

const addIntegrationDialogOpen = ref(false);

function handleIntegrationCreated() {
    // Close the add integration dialog
    addIntegrationDialogOpen.value = false;
}
</script>
